import { useQuery } from '@tanstack/react-query'
import { toast } from 'react-hot-toast'
import controllerDataConfigApi from '@/api/config/controllerDataConfig'
import { queryKeys } from '@/constant/common'
import { IoTypeMask } from '@/types/controller'
import type { InputOptions } from '@/types/hydraulic'
import { getApiErrorMessage } from '@/helpers'

/**
 * The decoders a flow sensor can be wired to: the interface's INPUT and SDI-12
 * decoders.
 *
 * Two requests, not one — `GET /api/Controller/{id}` takes a single `type` per
 * query and ignores a combined mask, which is why the Controllers page gives
 * each type its own tab.
 *
 * `PageSize: 0` asks for the unpaged list, the same way the station form's
 * decoder picker does; a picker that stopped at the first 20 decoders would
 * quietly hide the rest.
 */
const useSensorDecoderOptions = (interfaceId: number) =>
  useQuery({
    queryKey: [queryKeys.sensorDecoderOptions, interfaceId],
    queryFn: async (): Promise<InputOptions[]> => {
      const [input, sdi12] = await Promise.all(
        [IoTypeMask.Input, IoTypeMask.Sdi12].map((type) =>
          controllerDataConfigApi.getAll({
            interfaceId,
            PageIndex: 1,
            PageSize: 0,
            type,
          }),
        ),
      )

      const rows = [
        ...(input?.data?.data ?? input?.data ?? []),
        ...(sdi12?.data?.data ?? sdi12?.data ?? []),
      ] as any[]

      return rows
        .filter(Boolean)
        .map((row) => ({
          id: String(row.id ?? ''),
          name: String(row.name ?? ''),
          address: Number(row.address),
          index: Number(row.index),
          interfaceId: Number(row.interfaceId ?? interfaceId),
          decoderTypeId: Number(row.decoderTypeId),
          // Only a control hub fills these in; a decoder wired straight to the
          // controller comes back without them, and the picker then shows no
          // controller line.
          twiId: row.twiId ?? null,
          twiName: row.twiName,
          twiCustomName: row.twiCustomName,
        }))
        .filter((row) => Number.isFinite(row.address) && Number.isFinite(row.index))
    },
    enabled: Number.isFinite(interfaceId) && interfaceId > 0,
    onError: (error: any) => {
      toast.error(getApiErrorMessage(error))
    },
  })

export default useSensorDecoderOptions
